import { useQuery, useMutation, UseQueryResult } from '@tanstack/react-query'
import analyticsService, { YearRangeParams } from '../services/analyticsService'
import type {
  DashboardOverviewResponse,
  HighYieldUnitsResponse,
  TopicTrendsSummaryResponse,
  SubjectUnitWeightageResponse,
  SubjectDifficultyTrendsResponse,
  ExamSummaryResponse,
  ExamsListResponse,
  UnitTopicBreakdownResponse,
  HighYieldTopicsResponse,
  TrendingTopicsResponse,
  MustStudyTopicsResponse,
  NextExamPredictionsResponse,
  UnitSummaryResponse,
  UnitExamTimelineResponse,
  TopicDetailedStatsResponse,
  TopicExamTimelineResponse,
  TopicComparisonRequest,
  TopicComparisonResponse,
  ExamTopicCoverageResponse,
  YearOverYearComparisonResponse,
  SubjectCode,
} from '../types'

// Query Key Factory
export const pyqAnalyticsKeys = {
  all: ['pyq-analytics'] as const,

  // Dashboard
  dashboard: () => [...pyqAnalyticsKeys.all, 'dashboard'] as const,
  dashboardOverview: (params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.dashboard(), 'overview', params] as const,

  // High Yield
  highYield: () => [...pyqAnalyticsKeys.all, 'high-yield'] as const,
  highYieldUnits: (params?: { subjectCode?: string; limit?: number } & YearRangeParams) =>
    [...pyqAnalyticsKeys.highYield(), 'units', params] as const,
  highYieldTopics: (params?: { subjectCode?: string; limit?: number } & YearRangeParams) =>
    [...pyqAnalyticsKeys.highYield(), 'topics', params] as const,
  topImportanceTopics: (params?: { subjectCode?: string; limit?: number }) =>
    [...pyqAnalyticsKeys.all, 'topics', 'top-importance', params] as const,

  // Topic Trends
  trends: () => [...pyqAnalyticsKeys.all, 'trends'] as const,
  topicTrendsSummary: (params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.trends(), 'summary', params] as const,
  increasingTopics: (params?: { limit?: number; subjectCode?: string } & YearRangeParams) =>
    [...pyqAnalyticsKeys.trends(), 'increasing', params] as const,
  decreasingTopics: (params?: { limit?: number; subjectCode?: string } & YearRangeParams) =>
    [...pyqAnalyticsKeys.trends(), 'decreasing', params] as const,
  yearOverYear: (params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.trends(), 'year-over-year', params] as const,

  // Subject Analytics
  subjects: () => [...pyqAnalyticsKeys.all, 'subjects'] as const,
  subjectUnitWeightage: (subjectCode: string, params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.subjects(), subjectCode, 'unit-weightage', params] as const,
  subjectDifficultyTrends: (subjectCode: string, params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.subjects(), subjectCode, 'difficulty-trends', params] as const,

  // Unit Analytics
  units: () => [...pyqAnalyticsKeys.all, 'units'] as const,
  unitTopicBreakdown: (unitCode: string, params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.units(), unitCode, 'topic-breakdown', params] as const,
  unitSummary: (unitCode: string, params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.units(), unitCode, 'summary', params] as const,
  unitExamTimeline: (unitCode: string, params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.units(), unitCode, 'exam-timeline', params] as const,

  // Topic Analytics
  topics: () => [...pyqAnalyticsKeys.all, 'topics'] as const,
  topicDetailedStats: (topicCode: string, params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.topics(), topicCode, 'detailed-stats', params] as const,
  topicExamTimeline: (topicCode: string, params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.topics(), topicCode, 'exam-timeline', params] as const,

  // Predictions
  predictions: () => [...pyqAnalyticsKeys.all, 'predictions'] as const,
  mustStudyTopics: (params?: { limit?: number; subjectCode?: string } & YearRangeParams) =>
    [...pyqAnalyticsKeys.predictions(), 'must-study', params] as const,
  nextExamPredictions: (params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.predictions(), 'next-exam', params] as const,

  // Exams
  exams: () => [...pyqAnalyticsKeys.all, 'exams'] as const,
  examsList: (params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.exams(), 'list', params] as const,
  examSummary: (testCode: string) =>
    [...pyqAnalyticsKeys.exams(), testCode, 'summary'] as const,
  examTopicCoverage: (testCode: string) =>
    [...pyqAnalyticsKeys.exams(), testCode, 'topic-coverage'] as const,

  // Comparisons
  comparisons: () => [...pyqAnalyticsKeys.all, 'comparisons'] as const,
  topicComparison: (topicCodes: string[], params?: YearRangeParams) =>
    [...pyqAnalyticsKeys.comparisons(), 'topics', topicCodes, params] as const,
}

const STALE_TIME = 5 * 60 * 1000 // 5 minutes

// Dashboard Hooks
export function useDashboardOverview(
  params?: YearRangeParams
): UseQueryResult<DashboardOverviewResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.dashboardOverview(params),
    queryFn: () => analyticsService.getDashboardOverview(params),
    staleTime: STALE_TIME,
  })
}

// High Yield Hooks
export function useHighYieldUnits(
  params?: { subjectCode?: string; limit?: number } & YearRangeParams
): UseQueryResult<HighYieldUnitsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.highYieldUnits(params),
    queryFn: () => analyticsService.getHighYieldUnitsDetailed(params),
    staleTime: STALE_TIME,
  })
}

export function useHighYieldTopics(
  params?: { subjectCode?: string; limit?: number } & YearRangeParams
): UseQueryResult<HighYieldTopicsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.highYieldTopics(params),
    queryFn: () => analyticsService.getHighYieldTopics(params),
    staleTime: STALE_TIME,
  })
}

export function useTopImportanceTopics(
  params?: { subjectCode?: string; limit?: number }
): UseQueryResult<HighYieldTopicsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.topImportanceTopics(params),
    queryFn: () => analyticsService.getTopImportanceTopics(params),
    staleTime: STALE_TIME,
  })
}

// Trend Hooks
export function useTopicTrendsSummary(
  params?: YearRangeParams
): UseQueryResult<TopicTrendsSummaryResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.topicTrendsSummary(params),
    queryFn: () => analyticsService.getTopicTrendsSummary(params),
    staleTime: STALE_TIME,
  })
}

export function useIncreasingTopics(
  params?: { limit?: number; subjectCode?: string } & YearRangeParams
): UseQueryResult<TrendingTopicsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.increasingTopics(params),
    queryFn: () => analyticsService.getIncreasingTopics(params),
    staleTime: STALE_TIME,
  })
}

export function useDecreasingTopics(
  params?: { limit?: number; subjectCode?: string } & YearRangeParams
): UseQueryResult<TrendingTopicsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.decreasingTopics(params),
    queryFn: () => analyticsService.getDecreasingTopics(params),
    staleTime: STALE_TIME,
  })
}

export function useYearOverYearComparison(
  params?: YearRangeParams
): UseQueryResult<YearOverYearComparisonResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.yearOverYear(params),
    queryFn: () => analyticsService.getYearOverYearComparison(params),
    staleTime: STALE_TIME,
  })
}

// Subject Hooks
export function useSubjectUnitWeightage(
  subjectCode: SubjectCode,
  params?: YearRangeParams & {
    sortBy?: 'totalQuestions' | 'importanceScore' | 'avgQuestionsPerExam'
    sortOrder?: 'asc' | 'desc'
  }
): UseQueryResult<SubjectUnitWeightageResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.subjectUnitWeightage(subjectCode, params),
    queryFn: () => analyticsService.getSubjectUnitWeightage(subjectCode, params),
    staleTime: STALE_TIME,
    enabled: !!subjectCode,
  })
}

export function useSubjectDifficultyTrends(
  subjectCode: SubjectCode,
  params?: YearRangeParams
): UseQueryResult<SubjectDifficultyTrendsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.subjectDifficultyTrends(subjectCode, params),
    queryFn: () => analyticsService.getSubjectDifficultyTrends(subjectCode, params),
    staleTime: STALE_TIME,
    enabled: !!subjectCode,
  })
}

// Unit Hooks
export function useUnitTopicBreakdown(
  unitCode: string,
  params?: YearRangeParams & {
    sortBy?: 'totalQuestions' | 'importanceScore' | 'appearanceFrequency'
  }
): UseQueryResult<UnitTopicBreakdownResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.unitTopicBreakdown(unitCode, params),
    queryFn: () => analyticsService.getUnitTopicBreakdown(unitCode, params),
    staleTime: STALE_TIME,
    enabled: !!unitCode,
  })
}

export function useUnitSummary(
  unitCode: string | null,
  params?: YearRangeParams
): UseQueryResult<UnitSummaryResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.unitSummary(unitCode || '', params),
    queryFn: () => analyticsService.getUnitSummary(unitCode!, params),
    staleTime: STALE_TIME,
    enabled: !!unitCode,
  })
}

export function useUnitExamTimeline(
  unitCode: string | null,
  params?: YearRangeParams
): UseQueryResult<UnitExamTimelineResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.unitExamTimeline(unitCode || '', params),
    queryFn: () => analyticsService.getUnitExamTimeline(unitCode!, params),
    staleTime: STALE_TIME,
    enabled: !!unitCode,
  })
}

// Topic Hooks
export function useTopicDetailedStats(
  topicCode: string | null,
  params?: YearRangeParams
): UseQueryResult<TopicDetailedStatsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.topicDetailedStats(topicCode || '', params),
    queryFn: () => analyticsService.getTopicDetailedStats(topicCode!, params),
    staleTime: STALE_TIME,
    enabled: !!topicCode,
  })
}

export function useTopicExamTimeline(
  topicCode: string | null,
  params?: YearRangeParams
): UseQueryResult<TopicExamTimelineResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.topicExamTimeline(topicCode || '', params),
    queryFn: () => analyticsService.getTopicExamTimeline(topicCode!, params),
    staleTime: STALE_TIME,
    enabled: !!topicCode,
  })
}

// Predictions Hooks
export function useMustStudyTopics(
  params?: { limit?: number; subjectCode?: string } & YearRangeParams
): UseQueryResult<MustStudyTopicsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.mustStudyTopics(params),
    queryFn: () => analyticsService.getMustStudyTopics(params),
    staleTime: STALE_TIME,
  })
}

export function useNextExamPredictions(
  params?: YearRangeParams
): UseQueryResult<NextExamPredictionsResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.nextExamPredictions(params),
    queryFn: () => analyticsService.getNextExamPredictions(params),
    staleTime: STALE_TIME,
  })
}

// Exam Hooks
export function useExamsList(
  params?: YearRangeParams
): UseQueryResult<ExamsListResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.examsList(params),
    queryFn: () => analyticsService.getExamsList(params),
    staleTime: STALE_TIME,
  })
}

export function useExamSummary(
  testCode: string | null
): UseQueryResult<ExamSummaryResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.examSummary(testCode || ''),
    queryFn: () => analyticsService.getExamSummary(testCode!),
    staleTime: STALE_TIME,
    enabled: !!testCode,
  })
}

export function useExamTopicCoverage(
  testCode: string | null
): UseQueryResult<ExamTopicCoverageResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.examTopicCoverage(testCode || ''),
    queryFn: () => analyticsService.getExamTopicCoverage(testCode!),
    staleTime: STALE_TIME,
    enabled: !!testCode,
  })
}

// Comparison Hooks
export function useTopicComparison() {
  return useMutation({
    mutationFn: (request: TopicComparisonRequest) => analyticsService.compareTopics(request),
  })
}

export function useTopicComparisonQuery(
  topicCodes: string[],
  params?: YearRangeParams
): UseQueryResult<TopicComparisonResponse, Error> {
  return useQuery({
    queryKey: pyqAnalyticsKeys.topicComparison(topicCodes, params),
    queryFn: () =>
      analyticsService.compareTopics({
        topicCodes,
        yearRangeStart: params?.yearRangeStart,
        yearRangeEnd: params?.yearRangeEnd,
      }),
    staleTime: STALE_TIME,
    enabled: topicCodes.length >= 2,
  })
}
